import { DestroyRef, inject, Injectable } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  SCREENS_DESKTOP,
  SCREENS_TABLET_LG,
  SCREENS_DESKTOP_LG,
  SCREENS_TABLET,
} from '@snagajob/design-tokens';

@Injectable({
  providedIn: 'root',
})
export class ViewportChangeService {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly viewportRuler = inject(ViewportRuler);
  private readonly destroyRef = inject(DestroyRef);

  getInnerHeight(): Observable<number> {
    return this.viewportRuler.change(100).pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.viewportRuler.getViewportSize().height), // emit initial value
      switchMap(() => {
        return of(this.viewportRuler.getViewportSize().height);
      }),
    );
  }

  /**
   * Determines if view width is mobile based on the breakpoint 600px
   * @returns true if mobile view width, false if larger
   */
  isMobile(): Observable<boolean> {
    return this.breakpointObserver
      .observe(['(max-width: ' + SCREENS_TABLET + ')'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(
          this.isMobileWidth(this.viewportRuler.getViewportSize().width),
        ),
        map((state: BreakpointState) => {
          return state.matches;
        }),
      );
  }

  isTablet(): Observable<boolean> {
    return this.breakpointObserver
      .observe(['(min-width: ' + SCREENS_TABLET + ')'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(
          this.isTabletWidth(this.viewportRuler.getViewportSize().width),
        ),
        map((state: BreakpointState) => {
          return state.matches;
        }),
      );
  }

  isTabletLarge(): Observable<boolean> {
    return this.breakpointObserver
      .observe(['(min-width: ' + SCREENS_TABLET_LG + ')'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(
          this.isTabletWidth(this.viewportRuler.getViewportSize().width),
        ),
        map((state: BreakpointState) => {
          return state.matches;
        }),
      );
  }

  isDesktop(): Observable<boolean> {
    return this.breakpointObserver
      .observe(['(min-width: ' + SCREENS_DESKTOP + ')'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(
          this.isDesktopWidth(this.viewportRuler.getViewportSize().width),
        ),
        map((state: BreakpointState) => {
          return state.matches;
        }),
      );
  }

  isDesktopLarge(): Observable<boolean> {
    return this.breakpointObserver
      .observe(['(min-width: ' + SCREENS_DESKTOP_LG + ')'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(
          this.isDesktopWidth(this.viewportRuler.getViewportSize().width),
        ),
        map((state: BreakpointState) => {
          return state.matches;
        }),
      );
  }

  private isMobileWidth(viewWidth: number): boolean {
    return viewWidth < +SCREENS_TABLET.replace('px', '');
  }

  private isTabletWidth(viewWidth: number): boolean {
    return viewWidth >= +SCREENS_TABLET.replace('px', '');
  }

  private isDesktopWidth(viewWidth: number): boolean {
    return viewWidth >= +SCREENS_DESKTOP.replace('px', '');
  }
}
